import { useState, useCallback, useMemo } from "react";
import { SafetyBufferMode } from "../../../RampOptimization/algo/placement/safetyBuffer";
import { SortingStrategy } from "../sortingStrategies";
import {
  DEFAULT_QUICK_TURN_THRESHOLD_MINUTES,
  DEFAULT_ARRIVAL_STAGING_DWELL_MINUTES,
  DEFAULT_DEPARTURE_STAGING_DWELL_MINUTES,
  MIN_ARRIVAL_STAGING_MINUTES,
  MAX_ARRIVAL_STAGING_MINUTES,
  MIN_DEPARTURE_STAGING_MINUTES,
  MAX_DEPARTURE_STAGING_MINUTES,
  MIN_QUICK_TURN_THRESHOLD_MINUTES,
  MAX_QUICK_TURN_THRESHOLD_MINUTES,
} from "../types";

/**
 * Validation result for staging configuration values
 */
export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Validates arrival staging dwell time
 * @param value - Value to validate (in minutes)
 * @returns Validation result
 */
export function validateArrivalStagingDwell(value: number): ValidationResult {
  if (value < MIN_ARRIVAL_STAGING_MINUTES) {
    return {
      isValid: false,
      message: `Arrival staging must be at least ${MIN_ARRIVAL_STAGING_MINUTES} minutes`,
    };
  }
  if (value > MAX_ARRIVAL_STAGING_MINUTES) {
    return {
      isValid: false,
      message: `Arrival staging cannot exceed ${MAX_ARRIVAL_STAGING_MINUTES} minutes`,
    };
  }
  return { isValid: true };
}

/**
 * Validates departure staging dwell time
 * @param value - Value to validate (in minutes)
 * @returns Validation result
 */
export function validateDepartureStagingDwell(value: number): ValidationResult {
  if (value < MIN_DEPARTURE_STAGING_MINUTES) {
    return {
      isValid: false,
      message: `Departure staging must be at least ${MIN_DEPARTURE_STAGING_MINUTES} minutes`,
    };
  }
  if (value > MAX_DEPARTURE_STAGING_MINUTES) {
    return {
      isValid: false,
      message: `Departure staging cannot exceed ${MAX_DEPARTURE_STAGING_MINUTES} minutes`,
    };
  }
  return { isValid: true };
}

/**
 * Validates quick turn threshold
 * @param value - Value to validate (in minutes)
 * @returns Validation result
 */
export function validateQuickTurnThreshold(value: number): ValidationResult {
  if (value < MIN_QUICK_TURN_THRESHOLD_MINUTES) {
    return {
      isValid: false,
      message: `Quick turn threshold must be at least ${MIN_QUICK_TURN_THRESHOLD_MINUTES} minutes`,
    };
  }
  if (value > MAX_QUICK_TURN_THRESHOLD_MINUTES) {
    return {
      isValid: false,
      message: `Quick turn threshold cannot exceed ${MAX_QUICK_TURN_THRESHOLD_MINUTES} minutes`,
    };
  }
  return { isValid: true };
}

/**
 * Validates all staging configuration values
 * @param arrivalDwell - Arrival staging dwell time (minutes)
 * @param departureDwell - Departure staging dwell time (minutes)
 * @param quickTurnThreshold - Quick turn threshold (minutes)
 * @returns Validation result with combined message
 */
export function validateStagingConfig(
  arrivalDwell: number,
  departureDwell: number,
  quickTurnThreshold: number
): ValidationResult {
  const arrivalResult = validateArrivalStagingDwell(arrivalDwell);
  if (!arrivalResult.isValid) return arrivalResult;

  const departureResult = validateDepartureStagingDwell(departureDwell);
  if (!departureResult.isValid) return departureResult;

  const thresholdResult = validateQuickTurnThreshold(quickTurnThreshold);
  if (!thresholdResult.isValid) return thresholdResult;

  return { isValid: true };
}

export interface ParkingConfig {
  bufferMinutes: number;
  plannedMoveCap: number;
  allowTrapping: boolean;
  egressWeight: number;
  wasteWeight: number;
  structuralWeight: number;
  zonePriorityWeight: number;
  min_dwell_weight: number;
  sortingStrategy: SortingStrategy;

  /**
   * Enable arrival staging workflow
   *
   * When enabled, arriving aircraft go to staging area first for servicing
   * (unloading passengers/cargo, customs, refueling) before moving to permanent spot.
   *
   * Creates arrival segment: Landing → Staging (arrivalStagingDwellMinutes) → Permanent
   *
   * Default: false
   */
  enableArrivalStaging: boolean;

  /**
   * Enable departure staging workflow
   *
   * When enabled, departing aircraft move to staging area for final preparation
   * (boarding, final checks, paperwork) before departure.
   *
   * Creates departure segment: Permanent → Staging (departureStagingDwellMinutes) → Departure
   *
   * Default: false
   */
  enableDepartureStaging: boolean;

  /**
   * Duration for arrival staging operations (minutes)
   *
   * Time allocated for arrival servicing:
   * - Unloading passengers and cargo
   * - Customs/immigration processing
   * - Refueling operations
   * - Post-flight inspection and cleaning
   *
   * Default: DEFAULT_ARRIVAL_STAGING_DWELL_MINUTES (60 minutes / 1 hour)
   * Typical range: 45-90 minutes
   */
  arrivalStagingDwellMinutes: number;

  /**
   * Duration for departure staging operations (minutes)
   *
   * Time allocated for departure preparation:
   * - Boarding passengers
   * - Final safety checks
   * - Flight plan filing
   * - Pre-flight inspection
   *
   * Default: DEFAULT_DEPARTURE_STAGING_DWELL_MINUTES (30 minutes)
   * Typical range: 20-45 minutes
   */
  departureStagingDwellMinutes: number;

  /**
   * Minimum dwell time threshold for using staging workflow (minutes)
   *
   * Aircraft with total dwell time ≤ this threshold bypass staging
   * and are placed directly in permanent spots (quick turn handling).
   *
   * This prevents unnecessary staging moves for short visits.
   *
   * Default: DEFAULT_QUICK_TURN_THRESHOLD_MINUTES (120 minutes / 2 hours)
   * Typical range: 60-180 minutes
   *
   * Example:
   * - 90-minute quick turn: bypass staging (single permanent spot)
   * - 10-hour stay: use staging workflow
   */
  quickTurnThresholdMinutes: number;

  /**
   * Safety buffer mode for aircraft spacing
   */
  bufferMode?: SafetyBufferMode;

  /**
   * Tightness factor for safety buffers (0-10 scale)
   * Used with FIXED buffer mode
   */
  tightness?: number;

  /**
   * Wingspan multiplier for safety buffers
   * Used with WINGSPAN_BASED buffer mode
   */
  wingspanMultiplier?: number;
}

export interface UseParkingConfigOptions {
  bufferMinutes?: number;
  plannedMoveCap?: number;
  allowTrapping?: boolean;
  egressWeight?: number;
  wasteWeight?: number;
  structuralWeight?: number;
  zonePriorityWeight?: number;
  min_dwell_weight?: number;
  sortingStrategy?: SortingStrategy;
  enableArrivalStaging?: boolean;
  enableDepartureStaging?: boolean;
  arrivalStagingDwellMinutes?: number;
  departureStagingDwellMinutes?: number;
  quickTurnThresholdMinutes?: number;
}

export interface UseParkingConfigResult {
  config: ParkingConfig;
  setBufferMinutes: (value: number) => void;
  setPlannedMoveCap: (value: number) => void;
  setAllowTrapping: (value: boolean) => void;
  setEgressWeight: (value: number) => void;
  setWasteWeight: (value: number) => void;
  setStructuralWeight: (value: number) => void;
  setZonePriorityWeight: (value: number) => void;
  setMinDwellWeight: (value: number) => void;
  setSortingStrategy: (value: SortingStrategy) => void;
  setArrivalStagingDwellMinutes: (value: number) => void;
  setDepartureStagingDwellMinutes: (value: number) => void;
  setQuickTurnThresholdMinutes: (value: number) => void;
}

/**
 * Reusable hook for parking algorithm configuration
 * Used by both AutoPark and TemporalTest dev page
 */
export function useParkingConfig(
  defaults?: UseParkingConfigOptions
): UseParkingConfigResult {
  const [bufferMinutes, setBufferMinutes] = useState(
    defaults?.bufferMinutes ?? 15
  );
  const [plannedMoveCap, setPlannedMoveCap] = useState(
    defaults?.plannedMoveCap ?? 0
  );
  const [allowTrapping, setAllowTrapping] = useState(
    defaults?.allowTrapping ?? true
  );
  const [egressWeight, setEgressWeight] = useState(
    defaults?.egressWeight ?? 1.0
  );
  const [wasteWeight, setWasteWeight] = useState(defaults?.wasteWeight ?? 1.0);
  const [structuralWeight, setStructuralWeight] = useState(
    defaults?.structuralWeight ?? 0.1
  );
  const [zonePriorityWeight, setZonePriorityWeight] = useState(
    defaults?.zonePriorityWeight ?? 1.0
  );
  const [min_dwell_weight, setMinDwellWeight] = useState(
    defaults?.min_dwell_weight ?? 2.0
  );
  const [sortingStrategy, setSortingStrategy] = useState<SortingStrategy>(
    defaults?.sortingStrategy ?? "size-constrained"
  );
  const [arrivalStagingDwellMinutes, setArrivalStagingDwellMinutes] = useState(
    defaults?.arrivalStagingDwellMinutes ??
    (defaults?.enableArrivalStaging ? DEFAULT_ARRIVAL_STAGING_DWELL_MINUTES : 0)
  );
  const [departureStagingDwellMinutes, setDepartureStagingDwellMinutes] = useState(
    defaults?.departureStagingDwellMinutes ??
    (defaults?.enableDepartureStaging ? DEFAULT_DEPARTURE_STAGING_DWELL_MINUTES : 0)
  );
  const [quickTurnThresholdMinutes, setQuickTurnThresholdMinutes] = useState(
    defaults?.quickTurnThresholdMinutes ?? DEFAULT_QUICK_TURN_THRESHOLD_MINUTES
  );

  const config = useMemo(
    () => ({
      bufferMinutes,
      plannedMoveCap,
      allowTrapping,
      egressWeight,
      wasteWeight,
      structuralWeight,
      zonePriorityWeight,
      min_dwell_weight,
      sortingStrategy,
      enableArrivalStaging: arrivalStagingDwellMinutes > 0,
      enableDepartureStaging: departureStagingDwellMinutes > 0,
      arrivalStagingDwellMinutes,
      departureStagingDwellMinutes,
      quickTurnThresholdMinutes,
    }),
    [bufferMinutes, plannedMoveCap, allowTrapping, egressWeight, wasteWeight, structuralWeight, zonePriorityWeight, min_dwell_weight, sortingStrategy, arrivalStagingDwellMinutes, departureStagingDwellMinutes, quickTurnThresholdMinutes]
  );

  return {
    config,
    setBufferMinutes: useCallback(setBufferMinutes, []),
    setPlannedMoveCap: useCallback(setPlannedMoveCap, []),
    setAllowTrapping: useCallback(setAllowTrapping, []),
    setEgressWeight: useCallback(setEgressWeight, []),
    setWasteWeight: useCallback(setWasteWeight, []),
    setStructuralWeight: useCallback(setStructuralWeight, []),
    setZonePriorityWeight: useCallback(setZonePriorityWeight, []),
    setMinDwellWeight: useCallback(setMinDwellWeight, []),
    setSortingStrategy: useCallback(setSortingStrategy, []),
    setArrivalStagingDwellMinutes: useCallback(setArrivalStagingDwellMinutes, []),
    setDepartureStagingDwellMinutes: useCallback(setDepartureStagingDwellMinutes, []),
    setQuickTurnThresholdMinutes: useCallback(setQuickTurnThresholdMinutes, []),
  };
}
